// Shared API client configuration
import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { getMockToken } from "./mockData";

// Check if we should use mock API
const USE_MOCK_API =
	process.env.REACT_APP_USE_MOCK_API === "true" ||
	!process.env.REACT_APP_API_BASE_URL;

// Get base URL from environment variable
const API_BASE_URL =
	process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

// Cookie name - can be AUTH_TOKEN or ACCESS_TOKEN depending on backend
const TOKEN_COOKIE_NAME = process.env.REACT_APP_TOKEN_COOKIE_NAME || "access_token";

// Helper to get token from cookie (checks both AUTH_TOKEN and ACCESS_TOKEN)
const getAuthTokenFromCookie = (): string | null => {
	const cookies = document.cookie.split(";");
	const allCookies: Record<string, string> = {};
	
	for (const cookie of cookies) {
		const [name, value] = cookie.trim().split("=");
		if (name && value) {
			allCookies[name] = value;
			// Check for the configured cookie name
			if (name === TOKEN_COOKIE_NAME) {
				return value;
			}
		}
	}
	
	// Fallback: check for common token cookie names
	if (allCookies["ACCESS_TOKEN"]) {
		console.warn(
			`⚠️ Found ACCESS_TOKEN cookie but looking for ${TOKEN_COOKIE_NAME}.`,
			"\nEither update backend to use AUTH_TOKEN, or set REACT_APP_TOKEN_COOKIE_NAME=ACCESS_TOKEN in .env.local"
		);
		return allCookies["ACCESS_TOKEN"];
	}
	
	// Debug: log all cookies if token not found
	if (Object.keys(allCookies).length > 0) {
		console.log("📋 All cookies found:", Object.keys(allCookies));
	} else {
		console.log("📋 No cookies found in document.cookie");
	}
	
	return null;
};

// Create Axios instance
export const apiClient: AxiosInstance = axios.create({
	baseURL: API_BASE_URL,
	headers: {
		"Content-Type": "application/json",
	},
	withCredentials: true, // Important for cookie handling
});

// Request interceptor to add Authorization header
apiClient.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		// Don't add Authorization header for login endpoint (user is not authenticated yet)
		if (config.url?.includes("/login")) {
			return config;
		}

		if (USE_MOCK_API) {
			// For mock API, get token from localStorage
			const token = getMockToken();
			if (token) {
				config.headers.Authorization = token;
			}
		} else {
			// For real API: If backend uses cookie-based auth, the cookie is sent automatically
			// via withCredentials: true. We don't need to read it or add Authorization header.
			// If your backend requires Authorization header instead of cookies, uncomment below:
			// const token = getAuthTokenFromCookie();
			// if (token) {
			// 	config.headers.Authorization = `Bearer ${token}`;
			// }
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
	(response) => response,
	(error) => {
		// Handle CORS/preflight errors
		if (error.code === "ERR_NETWORK" || error.message?.includes("CORS")) {
			console.error(
				"❌ CORS Error: The backend must handle OPTIONS (preflight) requests and return proper CORS headers.\n",
				"Frontend origin:",
				window.location.origin,
				"\nBackend URL:",
				API_BASE_URL,
				"\n\n⚠️ The browser sends an OPTIONS request first (this is normal).",
				"\nYour backend must respond to OPTIONS with these headers:",
				"\n  - Access-Control-Allow-Origin: http://localhost:3000 (exact origin, not *)",
				"\n  - Access-Control-Allow-Credentials: true",
				"\n  - Access-Control-Allow-Methods: POST, GET, OPTIONS, etc.",
				"\n  - Access-Control-Allow-Headers: Content-Type, Authorization",
				"\n  - Status: 200 or 204"
			);
		}
		if (error.response?.status === 401) {
			console.error("Unauthorized access");
			if (window.location.pathname !== "/" && !window.location.pathname.includes("/login")) {
				window.location.href = "/";
			}
		}
		return Promise.reject(error);
	}
);

// Export helper function for use in auth service
export const getAuthTokenFromCookieHelper = getAuthTokenFromCookie;
export const TOKEN_COOKIE_NAME_EXPORT = TOKEN_COOKIE_NAME;
